import { z } from 'zod';
import { ReferralPatientSchema } from '../ReferralPatientSchema';
import { ReferralHealixPatientGenderSchema } from './ReferralHealixPatientGenderSchema';
import {
  dateValidation,
  emailValidation,
  nameValidation,
  phoneValidation,
  coerceString,
} from '../../../validation';

// "30101992" — the date of birth as the Healix export writes it, day, month
// and year with no separator.
const ddmmyyyy = /^(\d{2})(\d{2})(\d{4})$/;

// Healix sends the date of birth as DDMMYYYY, the referral form and every
// other caller as the ISO string JSON carries. Both are read here rather than
// by an endpoint, so the same body validates the same way whichever way in it
// came (docs/Schemas.md: the body is read by the schema).
//
// A DDMMYYYY that names no real day ("31022020", or "19921030" read the wrong
// way round) is left as sent for the date rule below to reject, rather than
// rolled over into the wrong date the way `new Date` would.
const readHealixDateOfBirth = (value: unknown) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return value;
  }

  const parts = `${value}`.match(ddmmyyyy);
  if (!parts) {
    return value;
  }

  const [, dd, mm, yyyy] = parts.map(Number);
  // Midnight UTC — the instant "1992-10-30" parses to, so the same day of
  // birth is the same value whichever form it was sent in
  const date = new Date(Date.UTC(yyyy, mm - 1, dd));

  return date.getUTCFullYear() === yyyy &&
    date.getUTCMonth() === mm - 1 &&
    date.getUTCDate() === dd
    ? date
    : value;
};

// Healix names four of the patient's fields its own way: the policy
// reference and the scheme name (PPL-2464), the member number its own
// system assigns (`patient.memberId`, PPL-3680) and the Healix product the
// member is on (`patient.product`, PPL-4214). Read here for the same reason
// as the date above — otherwise they are dropped as the unknown fields they
// are and never persisted. A value the body states under our own name wins
// over the alias.
const readHealixPatientFieldNames = (value: unknown) => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return value;
  }

  const { policyreference, clientname, memberId, product, ...patient } =
    value as Record<string, unknown>;
  if (
    policyreference === undefined &&
    clientname === undefined &&
    memberId === undefined &&
    product === undefined
  ) {
    return value;
  }

  return {
    ...patient,
    policyNumber: patient.policyNumber ?? policyreference,
    client: patient.client ?? clientname,
    externalId: patient.externalId ?? memberId,
    tenantProductId: patient.tenantProductId ?? product,
  };
};

// Healix posts straight to the API, and its export writes a reference that is
// all digits as the number it is — so the fields that carry one accept a
// number where a string is declared (coerceString), as they always have.
//
// The plain object shape, for the OpenAPI page to spread (`.shape`); what the
// referral schemas hold is the wrapped schema below, which reads the Healix
// fallback names first.
export const ReferralHealixPatientObjectSchema = ReferralPatientSchema.extend({
  firstname: nameValidation('Patient first name is required'),
  lastname: nameValidation('Patient surname is required'),
  email: emailValidation('Patient email is required'),
  phone: coerceString(phoneValidation()).optional(),
  gender: ReferralHealixPatientGenderSchema,
  dob: z
    .preprocess(
      readHealixDateOfBirth,
      dateValidation(
        'Patient date of birth should be a date, in DDMMYYYY or YYYY-MM-DD format',
      ),
    )
    .optional(),
  address: coerceString(
    z.string().trim().max(256, 'Patient must be at most 256 characters'),
  ).optional(),
  postcode: coerceString(
    z.string().trim().max(24, 'Patient must be at most 24 characters'),
  ).optional(),
  policyNumber: coerceString(z.string().trim()).optional(),
  // Healix `patient.memberId` — the member number Healix assigns, read from
  // that name too (see above). All-digit member numbers are exported as the
  // numbers they are, like every other reference Healix sends
  externalId: coerceString(z.string().trim()).optional(),
  // Healix `patient.claimNumber` — hoisted here from the top level of the
  // body, where the Healix body sends it (hoistPlanIdentifiersToPatient)
  claimNumber: coerceString(z.string().trim()).optional(),
  client: coerceString(z.string().trim()).optional(),
  // Healix `patient.product` (`perciSupport`, `cancerPreventionAndCare`) — the
  // product the member is on in Healix's own system, read from that name too
  // (see above). Healix owns the values, so any text is kept (trimmed) rather
  // than checked against a list. The export writes `null` for a member with
  // no product, which is read as none rather than failing the referral
  tenantProductId: z.preprocess(
    (value) => (value === null ? undefined : value),
    coerceString(z.string().trim()).optional(),
  ),
});

export const ReferralHealixPatientSchema = z.preprocess(
  readHealixPatientFieldNames,
  ReferralHealixPatientObjectSchema,
);

export type ReferralHealixPatient = z.infer<typeof ReferralHealixPatientSchema>;
